import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'

import translations from './footer-docs.translations'

/**
 * The three footer entries that open a document instead of leaving the campaign.
 * Also the `?view=` values they take in the URL, so a stale id from an old link resolves
 * to nothing rather than to the wrong document.
 */
export type FooterDocId = 'sources' | 'contributors' | 'press-kit'

export interface FooterDoc {
  /** `?view=<id>`, and the tracking name of the footer link that opens it. */
  id: FooterDocId
  /**
   * The footer link's label *and* the modal's title. One string on purpose: the link
   * names the document, so a second key could only ever disagree with it.
   */
  label: I18nDescriptor
}

/**
 * Authored here rather than in `PageFooterLinks.translations` because two components need
 * it — the footer row that opens each document and the modal that titles itself with the
 * same word. Legal Mentions stays in that translations file: it is a plain outbound link
 * with a URL, not a document this app renders.
 *
 * Order is the footer's, left to right (after Legal Mentions).
 */
export const FOOTER_DOCS: readonly FooterDoc[] = [
  {
    id: 'sources',
    label: translations.sourcesLabel,
  },
  {
    id: 'contributors',
    label: translations.contributorsLabel,
  },
  {
    id: 'press-kit',
    label: translations.pressKitLabel,
  },
]

/**
 * Resolves an id to its entry, or `null` for an absent/unknown one — which is also how the
 * modal asks "is this `?view=` value a document at all?": the documents are only part of
 * that key's value space (`useModalState`'s `ExtendedView`), and this list is the authority
 * on which part.
 */
export const footerDocById = (id: string | null): FooterDoc | null =>
  FOOTER_DOCS.find((doc) => doc.id === id) ?? null
